// v2 cattail — a marsh clump: headed stalks fanning out of one rootstock at
// varied leans, each brown sausage head topped with the thin flowering spike that
// real cattails carry, with two arching leaf blades wrapping the group. Variants
// turn the clump and shift the green. Ported 1:1 from age-of-mcp's v2/cattail.ts,
// using the library's shared `groundcover` (bladeArc, dirAt, tiltRot, h01, lerp)
// helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const TAU = Math.PI * 2;
const GREEN: [string, string][] = [
  ["#4c8a3e", "#5d9a48"],
  ["#558e3c", "#6aa850"],
];

function cattailParts(v: number): Part[] {
  const seed = v * 11 + 3;
  const yaw = v * 1.7 + 0.6;
  const [stalkCol, leafCol] = GREEN[v % GREEN.length]!;
  const parts: Part[] = [];
  // three headed stalks fanning from the rootstock
  const lens = [0.32, 0.26, 0.29];
  const tilts = [0.07, 0.22, 0.15];
  for (let i = 0; i < 3; i++) {
    const a = yaw + (i / 3) * TAU + groundcover.h01(seed, i) * 0.7;
    const len = lens[i]!, tilt = tilts[i]!;
    const d = groundcover.dirAt(a, tilt);
    const bx = Math.cos(a) * 0.025, bz = Math.sin(a) * 0.025;
    const at = (s: number): [number, number, number] =>
      [bx + d[0] * s, d[1] * s, bz + d[2] * s];
    parts.push({ geometry: new THREE.CylinderGeometry(0.005, 0.0075, len, 4),
      color: stalkCol, position: at(len / 2), rotation: groundcover.tiltRot(a, tilt) });
    // the brown sausage head
    parts.push({ geometry: new THREE.CylinderGeometry(0.016, 0.016, 0.07, 6),
      color: "#6e4a2c", position: at(len + 0.035), rotation: groundcover.tiltRot(a, tilt) });
    // thin flowering spike above it
    parts.push({ geometry: new THREE.CylinderGeometry(0.0022, 0.0036, 0.05, 3),
      color: "#c9b079", position: at(len + 0.095), rotation: groundcover.tiltRot(a, tilt) });
  }
  // two arching leaf blades wrapping the clump
  for (let i = 0; i < 2; i++) {
    const a = yaw + 1.1 + i * 2.6 + groundcover.h01(seed, i + 9) * 0.6;
    const arc = groundcover.bladeArc({
      x: Math.cos(a) * 0.03, z: Math.sin(a) * 0.03, ang: a,
      lean: groundcover.lerp(0.12, 0.2, groundcover.h01(seed, i + 5)),
      arch: groundcover.lerp(0.3, 0.45, groundcover.h01(seed, i + 7)),
      len: groundcover.lerp(0.26, 0.31, groundcover.h01(seed, i + 11)),
      w: 0.016, t: 0.004, color: leafCol,
    });
    parts.push(...arc.parts);
  }
  return parts;
}

export const cattailV2 = defineModel({
  id: "cattail-v2",
  batchId: "fable",
  name: "Cattail (v2)",
  tags: ["scenery","cattail","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly cattail.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.12,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => cattailParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => cattailParts(1) },
  ],
  animations: [],
});
